import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import useU2000AxiosAuthenticated from "../u2000Core/axios/U2000UseAxiosAuthenticated";
import { U2101ContextProvider } from "./U2101Context";
import { Outlet } from "react-router-dom";
import i18next from "i18next";
import { doInitLU, luHandleAppiError } from "./U2101Service";
import { useU2000InitLUHandlers } from "../u2000Core/hooks/handler/U2000UseInitLUHandlers";
import { U2000HandleApiError } from "../u2000Core/axios/U2000HandleApiError";
import { useToast } from "../u2000Core/context/toast/U2000ToastContext";
import { useU2000LogicalUnitContext } from "../u2000Core/context/logicalUnit/U2000LogicalUnitContext";

export const NS = "u2101";

export const U2101ROLES = {
  READ: "U2101:READ",
  ADD: "U2101:ADD",
  EDIT: "U2101:EDIT",
  DELETE: "U2101:DELETE",
  ACTIONS: "U2101:ACTIONS",
  AUDITS: "U2101:AUDITS",
  EXPORT: "U2101:EXPORT",
};
export type U2101ROLES = (typeof U2101ROLES)[keyof typeof U2101ROLES];

export interface U2101InitLU {
  launchSearch: boolean;
  hideSearch: boolean;
  nbRecordPerPage: number;
}

const U2101: React.FC = () => {
  const { i18n } = useTranslation();
  const [i18nReady, setI18nReady] = useState<boolean>(false);
  const { t } = useTranslation();
  const toast = useToast();
  const [initLUReady, setInitLUReady] = useState<boolean>(false);
  const { setLogicalUnitTitle, setLogicalUnitBreadcrumb } = useU2000LogicalUnitContext();
  const u2000AxiosAuthenticated = useU2000AxiosAuthenticated();
  const [initLUData, setInitLUData] = useState<U2101InitLU>({ launchSearch: false, hideSearch: true, nbRecordPerPage: 50 });

  // charge fichiers de traduction
  useEffect(() => {
    i18next.loadNamespaces(["u2101"], (err) => {
      if (err) return console.error("Error loading namespace:", err);
      setI18nReady(true);
    });
  }, [i18n]);

  // Gestion de l'initialisation de l'unité logique
  const handleInitLU = useU2000InitLUHandlers<U2101InitLU>({
    doInitLUFn: async () => {
      return await doInitLU(u2000AxiosAuthenticated);
    },
    onSuccess: (initLU: U2101InitLU) => {
      setInitLUData(initLU);
      setInitLUReady(true);
    },
    onError: (error: any) => {
      const errorResult = U2000HandleApiError(error, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
      setInitLUReady(true);
    },
  });

  // Initialisation de l'unité logique au montage du composant et apres quelques changements de langue
  useEffect(() => {
    handleInitLU.getInitLU();
  }, [t]);

  // Définit le titre et le breadcrumb pour l'unité logique
  useEffect(() => {
    if (!i18nReady || !initLUReady) return;

    const title = t("title", { ns: NS });
    setLogicalUnitTitle(title);

    return () => {
      setLogicalUnitTitle(null);
      setLogicalUnitBreadcrumb([]);
    };
  }, [i18nReady, initLUReady, t, setLogicalUnitTitle, setLogicalUnitBreadcrumb]);

  if (!i18nReady || !initLUReady) return null;

  return (
    <U2101ContextProvider initLU={initLUData}>
      <Outlet />
    </U2101ContextProvider>
  );
};

export default U2101;
